import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient'; // Assuming supabaseClient is in the parent directory
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DOCUMENT_STATUSES, DOCUMENT_STATUS_LABELS } from '../config/constants'; // Import statussen

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    console.error("Error formatting date:", e);
    return 'Ongeldige datum';
  }
};

// Sorting options keys and labels
const SORT_OPTIONS = {
  DATE_DESC: 'date_desc',
  DATE_ASC: 'date_asc',
  NAME_ASC: 'name_asc',
  NAME_DESC: 'name_desc',
  PROJECT_ASC: 'project_asc'
};

const SORT_LABELS = {
  [SORT_OPTIONS.DATE_DESC]: 'Datum (Nieuw - Oud)',
  [SORT_OPTIONS.DATE_ASC]: 'Datum (Oud - Nieuw)',
  [SORT_OPTIONS.NAME_ASC]: 'Naam (A-Z)',
  [SORT_OPTIONS.NAME_DESC]: 'Naam (Z-A)',
  [SORT_OPTIONS.PROJECT_ASC]: 'Project (A-Z)'
};

const ALL_STATUSES_KEY = 'all'; // Key for the 'all statuses' filter option
const ROWS_PER_PAGE_OPTIONS = [50, 100, 250, 500]; // Define options here

function Library() {
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState(SORT_OPTIONS.DATE_DESC);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES_KEY); // State for status filter
  const [page, setPage] = useState(0); // State for current page (0-indexed)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]); // State for rows per page, default 50

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('documents')
          .select('id, name, created_at, status, projects(id, name), profiles!reviewed_by(full_name)')
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error("Supabase fetch error:", fetchError);
          throw fetchError;
        }
        setAllDocuments(data || []);
      } catch (err) {
        console.error("Error fetching documents:", err);
        setError('Kon documenten niet laden. Controleer de console voor details.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  // Reset page to 0 when search term changes
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset page
  };

  // Reset page to 0 when sort option changes
  const handleSortChange = (event) => {
    setSortOption(event.target.value);
    setPage(0); // Reset page
  };

  // Reset page to 0 when status filter changes
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset page
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const processedDocuments = useMemo(() => {
    let filtered = allDocuments;
    if (statusFilter !== ALL_STATUSES_KEY) {
      filtered = allDocuments.filter(doc => doc.status === statusFilter);
    }

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.name.toLowerCase().includes(lowerSearchTerm) ||
        (doc.projects?.name && doc.projects.name.toLowerCase().includes(lowerSearchTerm))
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case SORT_OPTIONS.NAME_ASC:
          return a.name.localeCompare(b.name);
        case SORT_OPTIONS.NAME_DESC:
          return b.name.localeCompare(a.name);
        case SORT_OPTIONS.PROJECT_ASC:
          const projectA = a.projects?.name || '';
          const projectB = b.projects?.name || '';
          if (projectA === projectB) {
            return a.name.localeCompare(b.name);
          }
          return projectA.localeCompare(projectB);
        case SORT_OPTIONS.DATE_ASC:
          return new Date(a.created_at) - new Date(b.created_at);
        case SORT_OPTIONS.DATE_DESC:
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return sorted;
  }, [allDocuments, searchTerm, sortOption, statusFilter]);

  // Calculate documents for the current page
  const paginatedDocuments = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return processedDocuments.slice(startIndex, startIndex + rowsPerPage);
  }, [processedDocuments, page, rowsPerPage]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Documenten Bibliotheek
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Zoeken op naam of project..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: '300px', flexGrow: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="rows-per-page-label">Rijen p/p</InputLabel>
            <Select
              labelId="rows-per-page-label"
              id="rows-per-page-select"
              value={rowsPerPage}
              label="Rijen p/p"
              onChange={handleChangeRowsPerPage}
            >
              {ROWS_PER_PAGE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="status-filter-label">Filter Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter-select"
              value={statusFilter}
              label="Filter Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value={ALL_STATUSES_KEY}>Alle Statussen</MenuItem>
              {Object.entries(DOCUMENT_STATUS_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="sort-select-label">Sorteren op</InputLabel>
            <Select
              labelId="sort-select-label"
              id="sort-select"
              value={sortOption}
              label="Sorteren op"
              onChange={handleSortChange}
            >
              {Object.entries(SORT_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}

      {!loading && !error && (
        <>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table stickyHeader aria-label="document library table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Document Naam</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Beoordeeld door</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Aanmaakdatum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {processedDocuments.length === 0
                        ? 'Geen documenten gevonden.'
                        : 'Geen documenten gevonden die voldoen aan de filters op deze pagina.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedDocuments.map((doc) => (
                    <TableRow
                      key={doc.id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {doc.name}
                      </TableCell>
                      <TableCell>{doc.projects?.name || '-'}</TableCell>
                      <TableCell>{DOCUMENT_STATUS_LABELS[doc.status] || doc.status}</TableCell>
                      <TableCell>{doc.profiles?.full_name || 'nog niet beoordeeld'}</TableCell>
                      <TableCell>{formatDate(doc.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={processedDocuments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rijen per pagina:"
          />
        </>
      )}
    </Box>
  );
}

export default Library;
